import React, { useEffect, useMemo, useRef, useState } from 'react';
import { computeNiceTicks } from './chartUtils';

export interface AiProcessingTrendPoint {
  bucket: string;
  normal: number;
  abnormal: number;
  failed_processing: number;
}

type Props = {
  data: AiProcessingTrendPoint[];
  period: 'daily' | 'monthly';
  onPeriodChange: (value: 'daily' | 'monthly') => void;
};

const SERIES = [
  { key: 'normal' as const, label: 'Normal cases', color: '#16a34a', fill: 'rgba(22,163,74,.10)' },
  {
    key: 'abnormal' as const,
    label: 'Abnormal cases',
    color: '#d4a017',
    fill: 'rgba(212,160,23,.10)',
  },
  {
    key: 'failed_processing' as const,
    label: 'Failed processing',
    color: '#dc2626',
    fill: 'rgba(220,38,38,.08)',
  },
];

const HEIGHT = 260;
const PAD = { top: 16, right: 20, bottom: 34, left: 42 };

export default function AiProcessingTrendChart({ data, period, onPeriodChange }: Readonly<Props>) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(760);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  useEffect(() => {
    const update = () => setWidth(Math.max(wrapRef.current?.clientWidth || 760, 320));
    update();
    const observer = new ResizeObserver(update);
    if (wrapRef.current) {
      observer.observe(wrapRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const maxValue = Math.max(...data.flatMap(point => SERIES.map(series => point[series.key])), 0);
  const ticks = useMemo(() => computeNiceTicks(Math.max(maxValue, 1), 4), [maxValue]);
  const yMax = ticks[0] || 1;
  const plotW = width - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const xFor = (index: number) =>
    PAD.left + (data.length <= 1 ? plotW / 2 : (index / (data.length - 1)) * plotW);
  const yFor = (value: number) => PAD.top + plotH - (value / yMax) * plotH;

  const pathFor = (key: (typeof SERIES)[number]['key']) =>
    data
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${xFor(index)} ${yFor(point[key])}`)
      .join(' ');

  return (
    <section className="ai-trend-card">
      <div className="ai-trend-card__header">
        <h3>AI Processing Trend Over Time</h3>
        <select
          value={period}
          onChange={event => onPeriodChange(event.target.value as 'daily' | 'monthly')}
          aria-label="Trend period"
        >
          <option value="daily">Day</option>
          <option value="monthly">Month</option>
        </select>
      </div>

      <div
        ref={wrapRef}
        className="ai-trend-card__plot"
      >
        {data.length === 0 ? (
          <p className="analytics-empty">No data available</p>
        ) : (
          <svg
            width="100%"
            height={HEIGHT}
            viewBox={`0 0 ${width} ${HEIGHT}`}
          >
            <title>AI processing trend over time</title>
            {ticks.map(tick => (
              <g key={tick}>
                <line
                  x1={PAD.left}
                  x2={width - PAD.right}
                  y1={yFor(tick)}
                  y2={yFor(tick)}
                  stroke="#e8edf3"
                />
                <text
                  x={PAD.left - 9}
                  y={yFor(tick) + 4}
                  textAnchor="end"
                  className="ai-chart-axis"
                >
                  {tick}
                </text>
              </g>
            ))}

            {SERIES.map(series => (
              <path
                key={series.key}
                d={pathFor(series.key)}
                fill="none"
                stroke={series.color}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}

            {data.map((point, index) => (
              <g key={point.bucket}>
                <rect
                  x={xFor(index) - Math.max(plotW / Math.max(data.length, 1) / 2, 5)}
                  y={PAD.top}
                  width={Math.max(plotW / Math.max(data.length, 1), 10)}
                  height={plotH}
                  fill="transparent"
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                />
                {(index % Math.max(Math.ceil(data.length / 12), 1) === 0 ||
                  index === data.length - 1) && (
                  <text
                    x={xFor(index)}
                    y={HEIGHT - 10}
                    textAnchor="middle"
                    className="ai-chart-axis"
                  >
                    {point.bucket.slice(5)}
                  </text>
                )}
              </g>
            ))}

            {hoverIndex == null ? null : (
              <>
                <line
                  x1={xFor(hoverIndex)}
                  x2={xFor(hoverIndex)}
                  y1={PAD.top}
                  y2={PAD.top + plotH}
                  stroke="#94a3b8"
                  strokeDasharray="3 3"
                />
                {SERIES.map(series => (
                  <circle
                    key={series.key}
                    cx={xFor(hoverIndex)}
                    cy={yFor(data[hoverIndex][series.key])}
                    r="3.5"
                    fill={series.color}
                  />
                ))}
              </>
            )}
          </svg>
        )}

        {hoverIndex != null && data[hoverIndex] ? (
          <div
            className="ai-trend-tooltip"
            style={{
              left: `${Math.min(Math.max((xFor(hoverIndex) / width) * 100, 10), 86)}%`,
            }}
          >
            <strong>{data[hoverIndex].bucket}</strong>
            {SERIES.map(series => (
              <span key={series.key}>
                <i style={{ background: series.color }} />
                {series.label}: {data[hoverIndex][series.key]}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="ai-trend-card__legend">
        {SERIES.map(series => (
          <span key={series.key}>
            <i style={{ background: series.color }} />
            {series.label}
          </span>
        ))}
      </div>
    </section>
  );
}
